import {
  type VendorListResponse,
  VendorListResponse as VendorListResponseSchema,
} from '@uit/shared/schemas/vendor';
import { useState } from 'react';
import { useApi } from '../../lib/api.js';
import s from './ReferrerVendorsSection.module.scss';

// Curated referrer list (T-127): flag/un-flag vendors as referrers. Only
// flagged vendors appear in the onboarding "Referred by" dropdown. Un-flagging
// keeps history — engagements already referred by the vendor stay intact.
export function ReferrerVendorsSection() {
  const { data, error, loading, refetch } = useApi<VendorListResponse>(
    '/api/vendors?page_size=100',
    VendorListResponseSchema,
  );
  const [addId, setAddId] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const referrers = (data?.items ?? []).filter((v) => v.is_referrer);
  const candidates = (data?.items ?? []).filter((v) => !v.is_referrer);

  async function setFlag(id: string, isReferrer: boolean): Promise<void> {
    setBusyId(id);
    setActionError(null);
    try {
      const res = await fetch(`/api/vendors/${id}/referrer`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ is_referrer: isReferrer }),
      });
      if (!res.ok) {
        setActionError(
          res.status === 403
            ? 'You do not have permission to manage referrers.'
            : `Could not update the referrer list (${res.status}).`,
        );
        return;
      }
      setAddId('');
    } finally {
      setBusyId(null);
      void refetch();
    }
  }

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Referrer vendors</h2>
      <p className={s.sectionSubtitle}>
        Vendors who refer resources and earn a per-hour referral fee. Only vendors on this list
        appear in the onboarding &ldquo;Referred by&rdquo; dropdown. Removing one keeps existing
        referrals intact — it just leaves the dropdown.
      </p>

      {error && <div className={s.error}>Failed to load vendors: {error.message}</div>}
      {actionError && (
        <div className={s.error} role="alert">
          {actionError}
        </div>
      )}
      {!error && loading && !data && <div className={s.muted}>Loading…</div>}

      {data && referrers.length === 0 && (
        <div className={s.muted}>No referrer vendors yet — add one below.</div>
      )}

      {referrers.length > 0 && (
        <table className={s.table}>
          <thead>
            <tr>
              <th scope="col">Vendor</th>
              <th scope="col">Location</th>
              <th scope="col" style={{ textAlign: 'right' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {referrers.map((v) => (
              <tr key={v.id}>
                <td>{v.business_name}</td>
                <td>
                  {[v.business_address_city, v.business_address_state]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </td>
                <td>
                  <div className={s.actions}>
                    <button
                      type="button"
                      className={s.smallBtnDanger}
                      disabled={busyId === v.id}
                      onClick={() => void setFlag(v.id, false)}
                    >
                      Remove
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {data && (
        <div className={s.addRow}>
          <select
            className={s.select}
            aria-label="Vendor to add as referrer"
            value={addId}
            onChange={(e) => setAddId(e.target.value)}
          >
            <option value="">— Select a vendor —</option>
            {candidates.map((v) => (
              <option key={v.id} value={v.id}>
                {v.business_name}
                {v.business_address_city ? ` — ${v.business_address_city}` : ''}
                {v.business_address_state ? `, ${v.business_address_state}` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={s.addBtn}
            disabled={addId === '' || busyId !== null}
            onClick={() => void setFlag(addId, true)}
          >
            Add referrer
          </button>
        </div>
      )}
    </section>
  );
}
